import {
  Alert,
  Button,
  ButtonGroup,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Form } from 'src/components/common/form/Form';

import { Graph, KeyType, Role } from '../type';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onKeyCreated: (newKey: {
    name: string;
    type: string;
    role: string;
    entity: string;
    createdBy: string;
    createdAt: number;
    lastUsed: number;
    token: string;
    keyName: string;
  }) => void;
  keyTypes: KeyType[];
  roles: Role[];
  entities: Graph[];
  thisOrg: string;
}

export const CreateApiKeyModal = ({
  isOpen,
  onClose,
  onKeyCreated,
  keyTypes,
  roles,
  entities,
  thisOrg,
}: CreateApiKeyModalProps) => {
  const [newKey, setNewKey] = useState<{
    token: string;
    keyName: string;
  } | null>(null);
  const [selectedKeyType, setSelectedKeyType] = useState<string>(
    keyTypes[0]?.id || '',
  );
  const [selectedRole, setSelectedRole] = useState<string>(roles[0]?.id || '');
  const [selectedGraph, setSelectedGraph] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [selectedSubgraph, setSelectedSubgraph] = useState<string>('');
  const [keyName, setKeyName] = useState<string>('');

  const getGraphOptions = React.useMemo(() => {
    return entities.map((entity) => ({
      id: entity.id,
      name: entity.name,
    }));
  }, [entities]);

  const getVariantOptions = React.useMemo(() => {
    if (!selectedGraph) return [];
    const graph = entities.find((e) => e.id === selectedGraph);
    return (
      graph?.variants.map((variant) => ({
        id: variant.id,
        name: variant.name,
      })) || []
    );
  }, [selectedGraph, entities]);

  const getSubgraphOptions = React.useMemo(() => {
    if (!selectedVariant) return [];
    const graph = entities.find((e) => e.id === selectedGraph);
    const variant = graph?.variants.find((v) => v.id === selectedVariant);
    return (
      variant?.subgraphs.map((subgraph) => ({
        id: subgraph.id,
        name: subgraph.name,
      })) || []
    );
  }, [selectedGraph, selectedVariant, entities]);

  const handleCreate = () => {
    const keyType = keyTypes.find((type) => type.id === selectedKeyType);
    const role = roles.find((it) => it.id === selectedRole);
    let entityName = '';

    if (selectedKeyType === 'graph') {
      entityName = entities.find((e) => e.id === selectedGraph)?.name || '';
    } else if (selectedKeyType === 'variant') {
      const graph = entities.find((e) => e.id === selectedGraph);
      entityName =
        graph?.variants.find((v) => v.id === selectedVariant)?.name || '';
    } else if (selectedKeyType === 'subgraph') {
      const graph = entities.find((e) => e.id === selectedGraph);
      const variant = graph?.variants.find((v) => v.id === selectedVariant);
      entityName =
        variant?.subgraphs.find((s) => s.id === selectedSubgraph)?.name || '';
    } else {
      entityName = thisOrg;
    }

    const now = new Date().getTime();
    const newKeyData = {
      name: keyName,
      type: keyType?.id || '',
      role: role?.name || '',
      entity: entityName,
      createdBy: 'Current User',
      createdAt: now,
      lastUsed: now,
      token: Math.random().toString(5),
      keyName,
    };

    setNewKey(newKeyData);
    onKeyCreated(newKeyData);
  };

  const handleClose = () => {
    setNewKey(null);
    setSelectedKeyType(keyTypes[0]?.id || '');
    setSelectedRole(roles[0]?.id || '');
    setSelectedGraph('');
    setSelectedVariant('');
    setSelectedSubgraph('');
    setKeyName('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Create new API key</ModalTitle>
          <ModalDescription>
            API keys provide access to your organization's resources. Keep them
            secure.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {newKey ? (
            <>
              <Alert status="info">
                Make sure to copy your API key now. You won't be able to see it
                again.
              </Alert>
              <Code canCopy className="flex justify-between rounded p-3">
                {newKey.token}
              </Code>
            </>
          ) : (
            <Form
              className="flex flex-col gap-2 p-4"
              onSubmit={(e) => e.preventDefault()}
            >
              <FormControl isRequired>
                <FormLabel>Key Name</FormLabel>
                <Input
                  name="keyName"
                  placeholder="e.g., Development API Key"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Key Type</FormLabel>
                <Select
                  name="keyType"
                  value={selectedKeyType}
                  onChange={(e) => {
                    setSelectedKeyType(e.target.value);
                    setSelectedGraph('');
                    setSelectedVariant('');
                    setSelectedSubgraph('');
                  }}
                >
                  {keyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {
                    keyTypes.find((type) => type.id === selectedKeyType)
                      ?.description
                  }
                </FormHelperText>
              </FormControl>
              {selectedKeyType !== 'organization' && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Graph</FormLabel>
                    <Select
                      name="graph"
                      value={selectedGraph}
                      onChange={(e) => {
                        setSelectedGraph(e.target.value);
                        setSelectedVariant('');
                        setSelectedSubgraph('');
                      }}
                    >
                      <option value="">Select a Graph</option>
                      {getGraphOptions.map((graph) => (
                        <option key={graph.id} value={graph.id}>
                          {graph.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  {(selectedKeyType === 'variant' ||
                    selectedKeyType === 'subgraph') &&
                    selectedGraph && (
                      <FormControl isRequired>
                        <FormLabel>Variant</FormLabel>
                        <Select
                          name="variant"
                          value={selectedVariant}
                          onChange={(e) => {
                            setSelectedVariant(e.target.value);
                            setSelectedSubgraph('');
                          }}
                        >
                          <option value="">Select a Variant</option>
                          {getVariantOptions.map((variant) => (
                            <option key={variant.id} value={variant.id}>
                              {variant.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  {selectedKeyType === 'subgraph' && selectedVariant && (
                    <FormControl isRequired>
                      <FormLabel>Subgraph</FormLabel>
                      <Select
                        name="subgraph"
                        value={selectedSubgraph}
                        onChange={(e) => setSelectedSubgraph(e.target.value)}
                      >
                        <option value="">Select a Subgraph</option>
                        {getSubgraphOptions.map((subgraph) => (
                          <option key={subgraph.id} value={subgraph.id}>
                            {subgraph.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  name="role"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {roles.find((role) => role.id === selectedRole)?.description}
                </FormHelperText>
              </FormControl>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            {newKey ? (
              <Button variant="primary" onClick={handleClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="secondary" type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" onClick={handleCreate}>
                  Create
                </Button>
              </>
            )}
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
